import { ReactElement, useEffect, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { globalToLocalForce } from '@scene'
import { useDebouncedCallback } from 'use-debounce'
import {
  TextField as MuiTextField,
  InputProps,
  OutlinedTextFieldProps,
  InputAdornment,
} from '@mui/material'
import FormControl from '../FormControl'
import NumberInput from '../NumberInput'
import { formatLocalForce } from './utils'

interface Props extends Omit<OutlinedTextFieldProps, 'variant' | 'type'> {
  name: string
  domainFieldName: string
  placeholder?: string
  label?: string
  inputLabel?: string
  tooltip?: TooltipContents
  InputProps?: InputProps
  'data-cy'?: string
}

const LoadForceField = ({
  name,
  domainFieldName,
  rows = 1,
  multiline = false,
  autoFocus = false,
  placeholder = '',
  disabled = false,
  label = '',
  inputLabel,
  InputProps = {},
  tooltip,
  'data-cy': dataCy,
  ...textFieldProps
}: Props): ReactElement => {
  const { control, watch } = useFormContext()
  const {
    field: { onChange, onBlur, name: fieldName, value, ref },
    fieldState: { error },
  } = useController({ name, control })
  const [internalValue, setInternalValue] = useState<string | number>('')
  const domain = watch(domainFieldName)

  useEffect(() => {
    if (!domain) return
    setInternalValue(formatLocalForce(value, domain))
  }, [domain?.guid, value.z])

  const onChangeDebounced = useDebouncedCallback(value => {
    onChange(
      globalToLocalForce(
        {
          x: 0,
          y: 0,
          z: value * 1000,
        },
        domain,
      ),
    )
  }, 100)

  useEffect(() => {
    if (isNaN(Number(internalValue))) return
    onChangeDebounced(internalValue)
  }, [internalValue])

  return (
    <FormControl
      data-cy={dataCy}
      label={!inputLabel ? label : undefined}
      error={error?.message}
      tooltip={tooltip}
    >
      <MuiTextField
        variant="outlined"
        onChange={event => setInternalValue(event.target.value)}
        onBlur={onBlur}
        type="text"
        value={internalValue}
        placeholder={placeholder}
        autoFocus={autoFocus}
        name={fieldName}
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        inputRef={ref}
        size="small"
        InputProps={{
          // eslint-disable-next-line
          inputComponent: NumberInput as any,
          endAdornment: <InputAdornment position="end">kN</InputAdornment>,
          ...InputProps,
        }}
        label={inputLabel}
        data-cy={dataCy}
        {...textFieldProps}
      />
    </FormControl>
  )
}

export default LoadForceField
